// src/contexts/ThemeContext.tsx - ADMIN-ONLY COMPANY-WIDE THEME
import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ThemeProvider as MUIThemeProvider, createTheme, Theme, alpha, lighten, darken, getContrastRatio } from '@mui/material/styles';
import { PaletteMode } from '@mui/material';
import api from '../services/api';
import { useAuth } from './AuthContext';

// Theme preset definitions
export interface ThemeColors {
  primary: string;
  secondary: string;
  success?: string;
  warning?: string;
  error?: string;
  info?: string;
}

export interface ThemePreset {
  id: string;
  name: string;
  colors: ThemeColors;
}

export const themePresets: ThemePreset[] = [
  {
    id: 'navy-blue',
    name: 'Professional Navy',
    colors: {
      primary: '#0D47A1',
      secondary: '#FFC107',
      success: '#4CAF50',
      warning: '#FF9800',
      error: '#F44336',
      info: '#2196F3',
    },
  },
  {
    id: 'ocean-teal',
    name: 'Ocean Teal',
    colors: {
      primary: '#00838F',
      secondary: '#FFB300',
      success: '#43A047',
      warning: '#FB8C00',
      error: '#E53935',
      info: '#039BE5',
    },
  },
  {
    id: 'forest-green',
    name: 'Forest Green',
    colors: {
      primary: '#2E7D32',
      secondary: '#FF6F00',
      success: '#66BB6A',
      warning: '#FFA726',
      error: '#EF5350',
      info: '#42A5F5',
    },
  },
  {
    id: 'royal-purple',
    name: 'Royal Purple',
    colors: {
      primary: '#6A1B9A',
      secondary: '#FDD835',
      success: '#66BB6A',
      warning: '#FFA726',
      error: '#EF5350',
      info: '#42A5F5',
    },
  },
  {
    id: 'crimson-red',
    name: 'Crimson Red',
    colors: {
      primary: '#C62828',
      secondary: '#FFC107',
      success: '#4CAF50',
      warning: '#FF9800',
      error: '#D32F2F',
      info: '#2196F3',
    },
  },
  {
    id: 'slate-gray',
    name: 'Slate Gray',
    colors: {
      primary: '#455A64',
      secondary: '#FFC107',
      success: '#66BB6A',
      warning: '#FFA726',
      error: '#EF5350',
      info: '#42A5F5',
    },
  },
  {
    id: 'sunset-orange',
    name: 'Sunset Orange',
    colors: {
      primary: '#E65100',
      secondary: '#1976D2',
      success: '#43A047',
      warning: '#F57C00',
      error: '#D32F2F',
      info: '#0288D1',
    },
  },
  {
    id: 'midnight-blue',
    name: 'Midnight Blue',
    colors: {
      primary: '#1A237E',
      secondary: '#FFAB00',
      success: '#00C853',
      warning: '#FF6D00',
      error: '#DD2C00',
      info: '#2962FF',
    },
  },
];

interface ThemeContextType {
  mode: PaletteMode;
  currentPreset: ThemePreset;
  customColors: ThemeColors | null;
  loading: boolean;
  toggleColorMode: () => Promise<void>;
  setThemePreset: (presetId: string) => Promise<void>;
  setCustomColors: (colors: ThemeColors) => Promise<void>;
  resetToDefault: () => Promise<void>;
  canEditTheme: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within ThemeContextProvider');
  }
  return context;
};

interface ThemeContextProviderProps {
  children: React.ReactNode;
}

export const ThemeContextProvider: React.FC<ThemeContextProviderProps> = ({ children }) => {
  const { company, isRole, isAuthenticated } = useAuth();
  
  // Check if user can edit theme (company_admin or fleet_manager)
  const canEditTheme = isRole('company_admin') || isRole('fleet_manager');

  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<PaletteMode>('light');
  const [currentPreset, setCurrentPreset] = useState<ThemePreset>(themePresets[0]);
  const [customColors, setCustomColorsState] = useState<ThemeColors | null>(null);

  // Fetch company theme settings from backend
  useEffect(() => {
    const fetchCompanyTheme = async () => {
      const companyId = (company as any)?._id || (company as any)?.id;
      
      if (!isAuthenticated || !companyId) {
        setLoading(false);
        return;
      }

      try {
        const response = await api.get(`/companies/${companyId}/theme`);
        
        // Handle different response structures
        let themeData = response.data?.data || response.data;
        
        // If themeData is null/undefined, use defaults
        if (!themeData || typeof themeData !== 'object') {
          console.log('No theme data found, using defaults');
          setLoading(false);
          return;
        }

        // Apply theme mode
        if (themeData.mode) {
          setMode(themeData.mode as PaletteMode);
        }

        // Apply preset or custom colors
        if (themeData.customColors) {
          setCustomColorsState(themeData.customColors);
        } else if (themeData.presetId) {
          const preset = themePresets.find(p => p.id === themeData.presetId);
          if (preset) {
            setCurrentPreset(preset);
          }
        }
      } catch (error) {
        console.error('Failed to fetch company theme:', error);
        // Use default theme on error
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyTheme();
  }, [isAuthenticated, company]);

  // Toggle dark/light mode (admin only)
  const toggleColorMode = async () => {
    if (!canEditTheme) {
      console.warn('Only admins can change theme settings');
      return;
    }

    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);

    const companyId = (company as any)?._id || (company as any)?.id;
    if (!companyId) return;

    try {
      await api.put(`/companies/${companyId}/theme`, {
        mode: newMode,
        presetId: currentPreset.id,
        customColors: customColors,
      });
    } catch (error) {
      console.error('Failed to save theme mode:', error);
      // Revert on error
      setMode(mode);
    }
  };

  // Set theme preset (admin only)
  const setThemePreset = async (presetId: string) => {
    if (!canEditTheme) {
      console.warn('Only admins can change theme settings');
      return;
    }

    const preset = themePresets.find(p => p.id === presetId);
    if (!preset) return;

    setCurrentPreset(preset);
    setCustomColorsState(null); // Clear custom colors when selecting preset

    const companyId = (company as any)?._id || (company as any)?.id;
    if (!companyId) return;

    try {
      await api.put(`/companies/${companyId}/theme`, {
        mode,
        presetId: preset.id,
        customColors: null,
      });
    } catch (error) {
      console.error('Failed to save theme preset:', error);
    }
  };

  // Set custom colors (admin only)
  const setCustomColors = async (colors: ThemeColors) => {
    if (!canEditTheme) {
      console.warn('Only admins can change theme settings');
      return;
    }

    setCustomColorsState(colors);

    const companyId = (company as any)?._id || (company as any)?.id;
    if (!companyId) return;

    try {
      await api.put(`/companies/${companyId}/theme`, {
        mode,
        presetId: null,
        customColors: colors,
      });
    } catch (error) {
      console.error('Failed to save custom colors:', error);
    }
  };

  // Reset to default theme (admin only)
  const resetToDefault = async () => {
    if (!canEditTheme) {
      console.warn('Only admins can change theme settings');
      return;
    }

    setMode('light');
    setCurrentPreset(themePresets[0]);
    setCustomColorsState(null);

    const companyId = (company as any)?._id || (company as any)?.id;
    if (!companyId) return;

    try {
      await api.put(`/companies/${companyId}/theme`, {
        mode: 'light',
        presetId: themePresets[0].id,
        customColors: null,
      });
    } catch (error) {
      console.error('Failed to reset theme:', error);
    }
  };

  // Use custom colors if set, otherwise use preset colors
  // CRITICAL: Ensure we ALWAYS have valid colors
  const activeColors = customColors || currentPreset.colors || themePresets[0].colors;

  // Create MUI theme with current settings
  const theme = useMemo<Theme>(
    () => {
      // Double-check colors are valid before creating theme
      if (!activeColors?.primary || !activeColors?.secondary) {
        console.error('Invalid colors detected, using fallback');
        const fallback = themePresets[0].colors;
        
        return createTheme({
          palette: {
            mode,
            primary: { main: fallback.primary },
            secondary: { main: fallback.secondary },
            success: { main: fallback.success || '#4CAF50' },
            warning: { main: fallback.warning || '#FF9800' },
            error: { main: fallback.error || '#F44336' },
            info: { main: fallback.info || '#2196F3' },
          },
        });
      }
  
      const surface = mode === 'light' ? '#ffffff' : '#1e1e1e';
      const readableAccent = (color: string) => {
        let result = color;
        for (let step = 0; step < 24 && getContrastRatio(result, surface) < 4.5; step++) {
          result = mode === 'dark' ? lighten(result, 0.1) : darken(result, 0.1);
        }
        return result;
      };
      const primary = readableAccent(activeColors.primary);
      const secondary = readableAccent(activeColors.secondary);
      return createTheme({
        workspaceBrand: activeColors.primary,
        palette: {
          mode,
          primary: {
            main: primary,
            light: lighten(primary, 0.2),
            dark: readableAccent(darken(primary, 0.15)),
          },
          secondary: {
            main: secondary,
            light: lighten(secondary, 0.2),
            dark: readableAccent(darken(secondary, 0.15)),
          },
          success: {
            main: activeColors.success || '#4CAF50',
          },
          warning: {
            main: activeColors.warning || '#FF9800',
          },
          error: {
            main: activeColors.error || '#F44336',
          },
          info: {
            main: activeColors.info || '#2196F3',
          },
          background: {
            default: mode === 'light' ? '#f7fafc' : '#121212',
            paper: mode === 'light' ? '#ffffff' : '#1e1e1e',
          },
          text: {
            primary: mode === 'light' ? '#12263a' : '#ffffff',
            secondary: mode === 'light' ? '#6b7c93' : '#b0b0b0',
          },
        },
        typography: {
          fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
          h4: { fontWeight: 700 },
          h5: { fontWeight: 700 },
          h6: { fontWeight: 600 },
          button: { fontWeight: 600 },
        },
        components: {
          MuiTabs: { defaultProps: { variant: 'scrollable', scrollButtons: 'auto', allowScrollButtonsMobile: true } },
          MuiCard: {
            styleOverrides: {
              root: {
                boxShadow: mode === 'light'
                  ? `0 6px 16px ${alpha(activeColors.primary, 0.08)}`
                  : '0 6px 16px rgba(0,0,0,0.3)',
                borderRadius: 12,
                border: `1px solid ${alpha(primary, mode === 'light' ? 0.12 : 0.2)}`,
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: { textTransform: 'none', borderRadius: 10 },
            },
          },
          MuiTableHead: {
            styleOverrides: {
              root: {
                backgroundColor: alpha(primary, mode === 'light' ? 0.06 : 0.12),
              },
            },
          },
          MuiChip: {
            styleOverrides: {
              root: { fontWeight: 600 },
            },
          },
          MuiDrawer: {
            styleOverrides: {
              paper: {
                backgroundColor: mode === 'light' ? '#ffffff' : '#1e1e1e',
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                backgroundColor: activeColors.primary,
              },
            },
          },
        },
      });
    },
    [mode, activeColors]
  );

  const value: ThemeContextType = {
    mode,
    currentPreset,
    customColors,
    loading,
    toggleColorMode,
    setThemePreset,
    setCustomColors,
    resetToDefault,
    canEditTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      <MUIThemeProvider theme={theme}>{children}</MUIThemeProvider>
    </ThemeContext.Provider>
  );
};  